// src/contexts/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { getAuthToken, getUsername, getUserRole, getUserProfile, signOut, deriveCustomerNameFromEmail } from "../utils/auth";
import { fetchAdminUsers } from "../api/adminApi";
import { connectOrbieeChatSocket } from "../services/websocket";
import { showToast } from "../utils/toastManager";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

/**
 * The admin page's user list, or an empty one. Since 2026-10-18 the
 * `/auth_validate` reply no longer carries the list, so sign-in loads it the
 * way a page refresh always has. A failure here is not a failed sign-in: the
 * session is valid and only the admin page's data is missing, so the admin
 * is told once and the page shows an empty list (a refresh retries).
 */
const loadAdminUsersOrEmpty = async () => {
  try {
    const users = await fetchAdminUsers();
    return Array.isArray(users) ? users : [];
  } catch (error) {
    console.warn("[AuthContext] admin user list failed to load:", error);
    showToast({
      id: "admin-users-load",
      title: "Couldn't load the user list",
      message: "You're signed in. Refresh the admin page to try again.",
      severity: "error",
    });
    return [];
  }
};

// "Signed in elsewhere" (the session_takeover frame) is handled entirely by
// the avatar widget since 2026-10-28: the notice in the chat thread, the
// pinned toast and the one "Continue here" handler both buttons share live
// in AvatarLogoCode, next to the state they drive. This device stays signed
// in (until 2026-10-28 it signed itself out after a grace, and the toast
// lived here).

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    loading: true,
  });

  // On mount: restore auth state from cookies (returning / page-refresh user).
  // Does NOT dispatch authStateChanged — this is not a new login event.
  useEffect(() => {
    const initializeAuth = async () => {
      const token    = getAuthToken();
      const username = getUsername();
      const role     = getUserRole();

      if (token && username) {
        if (role === "C") {
          const profile = getUserProfile();
          setAuthState({
            user: profile
              ? { ...profile, role: profile.role || role }
              : {
                  username,
                  role,
                  customer_name: deriveCustomerNameFromEmail(username),
                },
            loading: false,
          });
          if (profile?.customer_id) {
            connectOrbieeChatSocket(profile.customer_id).catch(console.warn);
          }
        } else if (role === "A") {
          // Same loader as sign-in, same tolerance: the cookies are still
          // good, so a failed list is an empty admin page and a toast, not a
          // sign-out. (It used to sign out, which read as a broken login
          // whenever the users function was down — the user's call, 2026-10-19.)
          const profile = getUserProfile();
          const users = await loadAdminUsersOrEmpty();
          setAuthState({
            user: { ...(profile || {}), username: profile?.username || username, role, users },
            loading: false,
          });
        }
      } else {
        setAuthState({ user: null, loading: false });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Called by SignInModal after a successful Firebase login + validateUser.
   * Accepts the profile `validateUser` built — or, from AdminPage, the
   * current user with an edited user list.
   *
   * Sequence (in order):
   *  1. Update React auth state — all useAuth() consumers re-render immediately.
   *  2. Await WebSocket open (or timeout) — avatar trigger is deferred until WS is stable.
   *  3. Dispatch "authStateChanged" — ChatBot picks this up and plays the greeting.
   */
  const login = async (profile) => {
    // 0. Admins: fetch the user list unless the caller supplied one —
    //    AdminPage re-calls login() with the list it just edited, and a
    //    refetch must not overwrite that.
    const user =
      profile?.role === "A" && !Array.isArray(profile.users)
        ? { ...profile, users: await loadAdminUsersOrEmpty() }
        : profile;

    // 1. React state — components that use useAuth() see the user immediately.
    setAuthState({ user, loading: false });

    // 2. WebSocket — await open so avatar starts only after the connection is stable.
    if (profile?.customer_id) {
      await connectOrbieeChatSocket(profile.customer_id);
    }

    // 3. Notify the app — fired only after WS is confirmed open (or timed out).
    window.dispatchEvent(new CustomEvent("authStateChanged", { detail: user }));
  };

  const logout = () => {
    signOut();
    setAuthState({ user: null, loading: false });
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

